import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import BookingStatusBadge from './BookingStatusBadge';
import CancelBookingModal from './CancelBookingModal';
import type { ClientBooking } from '../../../types';

interface BookingCardProps {
  booking: ClientBooking;
}

const BookingCard: React.FC<BookingCardProps> = ({ booking }) => {
  const navigate = useNavigate();
  const [showCancelModal, setShowCancelModal] = useState(false);

  const formatDuration = (minutes: number) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours > 0 && mins > 0) {
      return `${hours}h ${mins}m`;
    } else if (hours > 0) {
      return `${hours}h`;
    } else {
      return `${mins}m`;
    }
  };

  const formatPrice = (cents: number, currency: string = 'USD') => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(cents / 100);
  };

  const date = booking.date_formatted;
  const time = booking.time_formatted;

  const getInitials = (name: string): string => {
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const handleCardClick = () => {
    navigate(`/u/bookings/${booking.id}`);
  };

  const handleCancelClick = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent card navigation
    setShowCancelModal(true);
  };

  const handleViewExpertClick = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent card navigation
    navigate(`/u/experts/${booking.expert.id}`);
  };

  return (
    <>
      <button
        onClick={handleCardClick}
        className="w-full text-left bg-white rounded-lg shadow-sm border border-gray-200 p-3 sm:p-5 hover:shadow-md hover:border-purple-200 transition-all active:scale-[0.99] cursor-pointer relative"
      >
        {/* Main content - Expert and Service info */}
        <div className="flex items-start space-x-3 sm:space-x-4 mb-3 sm:mb-0 sm:pr-32">
          {/* Expert Avatar */}
          <div className="flex-shrink-0">
            <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-gradient-to-br from-purple-400 to-pink-400 flex items-center justify-center text-white font-semibold text-xs sm:text-sm">
              {getInitials(booking.expert.display_name)}
            </div>
          </div>

          {/* Booking Details */}
          <div className="flex-1 min-w-0">
            <div className="flex items-start gap-2 mb-1 flex-wrap">
              <h3 className="text-base sm:text-lg font-semibold text-gray-900 truncate">
                {booking.expert.display_name}
              </h3>
              <BookingStatusBadge status={booking.status} />
            </div>

            <p className="text-sm font-medium text-purple-600 mb-2 truncate">
              {booking.service?.name || 'Service N/A'}
            </p>

            <div className="space-y-1 text-xs sm:text-sm text-gray-600">
              <div className="flex items-center">
                <svg
                  className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1.5 sm:mr-2 text-gray-400 flex-shrink-0"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                  />
                </svg>
                <span className="truncate">{date}</span>
              </div>
              <div className="flex items-center">
                <svg
                  className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1.5 sm:mr-2 text-gray-400 flex-shrink-0"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                <span className="truncate">
                  {time} {booking.service && `• ${formatDuration(booking.service.duration_min)}`}
                </span>
              </div>
              {booking.service && (
                <div className="flex items-center">
                  <svg
                    className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1.5 sm:mr-2 text-gray-400 flex-shrink-0"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  <span className="font-medium text-gray-900">
                    {formatPrice(booking.service.price_cents, booking.service.currency)}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Actions - Stacked on mobile, inline on desktop */}
        <div className="flex gap-2 sm:hidden pt-3 border-t border-gray-100">
          {booking.can_cancel && (
            <button
              onClick={handleCancelClick}
              className="flex-1 px-3 py-2 text-sm font-medium text-red-600 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-colors"
            >
              Cancel
            </button>
          )}
          <button
            onClick={handleViewExpertClick}
            className="flex-1 px-3 py-2 text-sm font-medium text-purple-600 bg-purple-50 border border-purple-200 rounded-lg hover:bg-purple-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 transition-colors"
          >
            View Expert
          </button>
        </div>

        {/* Actions - Desktop only (positioned on the right) */}
        <div className="hidden sm:flex flex-col items-end gap-2 absolute top-5 right-5">
          {booking.can_cancel && (
            <button
              onClick={handleCancelClick}
              className="px-3 py-1.5 text-sm font-medium text-red-600 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-colors"
            >
              Cancel
            </button>
          )}
          <button
            onClick={handleViewExpertClick}
            className="px-3 py-1.5 text-sm font-medium text-purple-600 bg-purple-50 border border-purple-200 rounded-lg hover:bg-purple-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 transition-colors"
          >
            View Expert
          </button>
        </div>

        {/* Notes section (if exists) */}
        {booking.notes && (
          <div className="mt-3 pt-3 sm:mt-4 sm:pt-4 border-t border-gray-100">
            <p className="text-xs font-medium text-gray-500 mb-1">Notes</p>
            <p className="text-xs sm:text-sm text-gray-700">{booking.notes}</p>
          </div>
        )}

        {/* Cancellation reason (if exists) */}
        {booking.status === 'cancelled' && booking.cancellation_reason && (
          <div className="mt-3 pt-3 sm:mt-4 sm:pt-4 border-t border-gray-100">
            <p className="text-xs font-medium text-red-600 mb-1">Cancellation Reason</p>
            <p className="text-xs sm:text-sm text-gray-700">{booking.cancellation_reason}</p>
          </div>
        )}
      </button>

      {/* Cancel Modal */}
      <CancelBookingModal
        booking={booking}
        isOpen={showCancelModal}
        onClose={() => setShowCancelModal(false)}
      />
    </>
  );
};

export default BookingCard;
